// Owns the extended open-ended block state: the chart's mode and zoom, the sort order, and which
// answer variants are open. Seeded from the question's params and re-seeded whenever any of them
// changes outside the block.
//
// The block answers for its whole subtree rather than for itself alone: every open variant is a
// report-param owner in its own right, and each hands its api up as it mounts. So the chart-mode
// normalization and the export payload are gathered here, across this block and every variant
// under it, and the shell only forwards what it is given.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getModelValue, getTrait } from '@aytm/app/stats/lib/stat-page'
import {
  calcLabelClickTarget,
  isRType as calcIsRType,
  isTraitType as calcIsTraitType
} from '@aytm/app/stats/page/components/lib/utils'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/oe/lib/display-range'
import { calcExpandable } from '@aytm/app/stats/page/question-views/oex/lib/props/variants'
import {
  calcChartParamsSection,
  calcCollapseSection,
  calcDefaultChartMode,
  calcSortParamsSection,
  calcZoomSection,
  resolveChartMode,
  resolveSortOrder,
  resolveUncollapsed
} from '@aytm/app/stats/page/question-views/oex/lib/report-params'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import useLoadingRows from '@aytm/app/stats/page/question-views/components/items-list/useLoadingRows'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export default function useOexState({
  params,
  paramsRevision,
  questionKey,
  type,
  format,
  answer,
  answerVariants,
  questionChartMode,
  defaultSortOrder,
  labelTarget,
  onReady,
  onStateChange,
  onAnswerVariantParamsChange,
  onAnswerVariantWcParamsChange,
  onUpdateData
}) {
  const context = { key: questionKey, type, format, answer, questionChartMode }

  const contextRef = useRef()
  contextRef.current = { ...context, defaultSortOrder, answerVariants }

  const seed = useCallback(source => {
    const { defaultSortOrder } = contextRef.current

    return {
      chartMode: resolveChartMode(source),
      zoomLevel: decodeZoomParam(source.z),
      sortOrder: resolveSortOrder(source.s, defaultSortOrder),
      uncollapsed: resolveUncollapsed(source.cb)
    }
    // everything it reads comes off the ref, so it never re-binds
  }, [])

  const [state, setState] = useState(() => seed(params || {}))

  const snapshot = useRef()
  snapshot.current = { state, context, labelTarget }

  // Every open variant hands its api up as it mounts; the block answers for them as well as for
  // itself, so the exporters reach the whole subtree through one call.
  const answerVariantApis = useRef({})
  const onAnswerVariantReady = useCallback((position, answerVariantApi, meta) => {
    if (null == answerVariantApi) delete answerVariantApis.current[position]
    else answerVariantApis.current[position] = { api: answerVariantApi, meta: meta || {} }
  }, [])

  // the mounted chart knows the axis it is showing; the exporters ask the state for it
  const chartApiRef = useRef(null)
  const onChartReady = useCallback(api => {
    chartApiRef.current = api
  }, [])

  const announceRefs = useRef()
  announceRefs.current = { onAnswerVariantParamsChange, onAnswerVariantWcParamsChange, onUpdateData }

  // the rows whose own round trip is out
  const { loadingRows, markRowLoading, onRowsLoaded } = useLoadingRows({ questionKey, answer })

  // A variant announces its own changes the way any block does; this block is what writes them
  // down, so the announcement is forwarded with the key the params hang off. A mode it settled on
  // that cannot read the payload it holds is a reload, and reloads are collected per variant.
  const onAnswerVariantStateChange = useCallback(
    (position, key, reason, options = {}) => {
      const entry = answerVariantApis.current[position]
      if (!entry) return

      const reload =
        'mode' === reason ||
        ('seed' === reason && options.needsUpdate) ||
        ('state' === reason && options.configChanged) ||
        Boolean(options.resetAnswer)

      if (reload) markRowLoading(position)

      // the fetch is answered against the params, so a reload that follows a write travels with it
      // and the shell chains the two
      if ('seed' !== reason || options.needsUpdate) {
        announceRefs.current.onAnswerVariantParamsChange?.(key, entry.api.getReportParamsSection?.() || {}, reload)
      } else if (reload) {
        announceRefs.current.onUpdateData?.([key])
      }
    },
    [markRowLoading]
  )

  const onAnswerVariantWcParams = useCallback(
    (id, params) => announceRefs.current.onAnswerVariantWcParamsChange?.(id, params),
    []
  )

  // every open variant, paired with whatever it answers - the ones that answer nothing drop out
  const gather = useCallback(
    read =>
      Object.entries(answerVariantApis.current).reduce((acc, [position, entry]) => {
        const value = read(entry.api, position, entry.meta)

        return null == value ? acc : acc.concat([[position, value]])
      }, []),
    []
  )

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}) =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = 'state'

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(() => {
    // the mode the chart opens on once the question and global defaults are applied -- what the
    // exporters draw by. The block itself has no stored mode; the question's is the whole story.
    const resolveExportChartMode = () => {
      const { context } = snapshot.current

      return calcDefaultChartMode({
        chartMode: context.questionChartMode,
        questionChartMode: context.questionChartMode,
        globalChartMode: getModelValue('chartMode'),
        answer: context.answer,
        percentsOnly: true === context.format?.percentsOnly,
        type: context.type
      })
    }

    // the axis the chart is showing. The rendered chart knows it, zoom included; the maths is the
    // fallback for a chart that has not drawn yet.
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const { state, context } = snapshot.current
      if (getTrait() && calcIsTraitType(context)) return null

      const extremes = chartApiRef.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        zoomLevel: zoomLevel || state.zoomLevel || getModelValue('zoomLevel'),
        globalChartMode: getModelValue('chartMode'),
        format: context.format,
        answer: context.answer,
        answerVariants: contextRef.current.answerVariants,
        rType: calcIsRType(context) ? getModelValue('rType') : 'cases',
        confidenceLevels: PageGlobals.CONFIDENCE_LEVELS
      })
    }

    return {
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => {
        const { state, context } = snapshot.current

        return {
          ...calcZoomSection(state.zoomLevel, getModelValue('zoomLevel')),
          ...calcChartParamsSection({
            chartMode: state.chartMode,
            questionChartMode: context.questionChartMode,
            format: context.format
          }),
          ...calcSortParamsSection(state.sortOrder, contextRef.current.defaultSortOrder),
          ...calcCollapseSection(state.uncollapsed)
        }
      },
      // pptx / summary bridge: what the block is showing, with every open variant spelled out
      getExportData: () => {
        const { state } = snapshot.current
        const chartModeResolved = resolveExportChartMode()

        return {
          chartModeResolved,
          displayRange: resolveDisplayRange(chartModeResolved, null),
          sortOrder: state.sortOrder,
          uncollapsed: state.uncollapsed,
          // each open variant is exported as a question of its own: what it is, plus what it is
          // showing -- the payload the coffee block used to compose off its model
          items: gather((answerVariantApi, position, meta) => ({
            ...meta,
            ...(answerVariantApi.getExportData?.() || {})
          })).map(([position, model]) => ({
            uid: model.uid ?? Number(position),
            position: Number(position),
            model
          }))
        }
      },
      getDisplayRange: resolveDisplayRange,
      // preset bridge: the param every open variant's cloud would end up with if the preset were
      // poured into it. Resolving commits nothing -- the shell writes the merged section down.
      getWCParam: preset =>
        gather(answerVariantApi =>
          answerVariantApi.getWcParamsSection?.(answerVariantApi.importWcPreset?.(preset))
        ).reduce((acc, [, section]) => Object.assign(acc, section), {}),
      // the answer variant a clicked row stands for, resolved against the question it hangs off
      getLabelClickTarget: position =>
        calcLabelClickTarget({
          ...snapshot.current.labelTarget,
          answerVariants: contextRef.current.answerVariants,
          position
        }),
      update: (change = {}, options) => {
        // which rows' own fetch came back is the shell's to report; everything else is the block's
        const { loadedRows, ...rest } = change

        if (loadedRows?.length) onRowsLoaded(loadedRows)
        if (Object.keys(rest).length) onModelChange(rest, options)
      }
    }
  }, [gather, onModelChange, onRowsLoaded])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  //
  // Keyed on the revision the shell bumps per host render, not on the contents of the params bag.
  // A host-driven render is a fresh read of the live model; a re-render React starts for itself
  // carries the revision it already had. Keying on the contents missed an undo restoring a value
  // the block had already moved away from locally, because the block's own write is silent and the
  // string it was handed had never changed.
  useEffect(() => {
    pendingReason.current = 'seed'
    setState(seed(params ?? {}))
  }, [params, paramsRevision, seed])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  // Opening or closing every variant at once is a set operation on the ones that can be opened,
  // not a toggle of what is currently open.
  const onToggleAll = useCallback(
    collapse => {
      const { answerVariants } = contextRef.current

      const positions = collapse
        ? []
        : calcExpandable({ answerVariants })
            .map(av => String(av.position))
            .sort()

      onModelChange({ uncollapsed: positions.join('.') })
    },
    [onModelChange]
  )

  const onChartModeChange = useCallback(chartMode => onModelChange({ chartMode }), [onModelChange])

  // the chart names the question it is zooming; the block has only its own to move
  const onZoomChange = useCallback((key, zoomLevel) => onModelChange({ zoomLevel }), [onModelChange])

  const onSortChange = useCallback(sortOrder => onModelChange({ sortOrder }), [onModelChange])

  const onToggle = useCallback(
    (position, collapsed) => {
      const open = new Set(snapshot.current.state.uncollapsed.split('.').filter(Boolean))

      if (collapsed) open.delete(String(position))
      else open.add(String(position))

      onModelChange({ uncollapsed: [...open].sort().join('.') })
    },
    [onModelChange]
  )

  return {
    ...state,
    loadingRows,
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onSortChange,
    onAnswerVariantReady,
    onAnswerVariantStateChange,
    onAnswerVariantWcParams,
    onToggle,
    onToggleAll,
    onModelChange
  }
}
